import { auth } from '../firebase';
import { AnalystSnapshotResponse } from '../types/aiAnalyst';

const endpointBase =
  import.meta.env?.VITE_AI_ENDPOINT_BASE_URL ||
  import.meta.env?.VITE_MARKET_DATA_ENDPOINT_BASE_URL ||
  import.meta.env?.VITE_QUOTE_ENDPOINT_BASE_URL ||
  '';

const getFunctionUrl = (functionName: 'analystSnapshot') => {
  if (!endpointBase) {
    return `/${functionName}`;
  }

  try {
    const url = new URL(endpointBase);
    // Cloud Run function hostnames usually start with the function name,
    // e.g. https://getquote-<hash>-<region>.a.run.app.
    if (url.hostname.endsWith('.a.run.app') && url.hostname.includes('-')) {
      const hostParts = url.hostname.split('-');
      hostParts[0] = functionName.toLowerCase();
      url.hostname = hostParts.join('-');
      url.pathname = '/';
      url.search = '';
      return url.toString();
    }
  } catch {
    // Fall through to path-based URL construction for non-standard endpoint formats.
  }

  return `${endpointBase.replace(/\/+$/, '')}/${functionName}`;
};

const parseJsonResponse = async (response: Response) => {
  const body = await response.text();
  try {
    return body ? JSON.parse(body) : null;
  } catch {
    if (!response.ok) {
      throw new Error(`AI analyst request failed (${response.status}).`);
    }
    throw new Error('AI analyst response was not valid JSON.');
  }
};

export const fetchAnalystSnapshot = async (): Promise<AnalystSnapshotResponse> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const response = await fetch(getFunctionUrl('analystSnapshot'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });

  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(json?.error || 'Failed to fetch snapshot');
  return json.snapshot;
};
